import { useState } from 'react';

export interface SortableColumnHeaderProps<K extends string = string> {
  label: string;
  sortKey: K;
  currentSort?: string | undefined;
  currentDir?: 'asc' | 'desc' | undefined;
  onSort: (key: K) => void;
  align?: 'left' | 'right';
  /**
   * flush: removes left padding for columns that sit flush against a
   * checkbox/gutter cell. The grid container owns the column boundary;
   * this prop lets the header text align with data rows without an
   * arbitrary pixel override.
   */
  flush?: boolean;
}

function SortArrow({ dir }: { dir: 'asc' | 'desc' | undefined }) {
  return (
    <svg width={8} height={10} viewBox="0 0 8 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M4 1L1 4H7L4 1Z" fill={dir === 'asc' ? 'var(--evo-cyan)' : 'var(--evo-fg-4)'} />
      <path d="M4 9L7 6H1L4 9Z" fill={dir === 'desc' ? 'var(--evo-cyan)' : 'var(--evo-fg-4)'} />
    </svg>
  );
}

export function SortableColumnHeader<K extends string = string>({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
  align = 'left',
  flush = false,
}: SortableColumnHeaderProps<K>) {
  const [focused, setFocused] = useState(false);
  const active = currentSort === sortKey;
  const dir = active ? currentDir : undefined;

  return (
    <button
      type="button"
      role="columnheader"
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
      onClick={() => onSort(sortKey)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        gap: '4px',
        width: '100%',
        padding: `8px 12px 8px ${flush ? '0' : '12px'}`,
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        cursor: 'pointer',
        fontFamily: 'var(--evo-font)',
        fontSize: 'var(--evo-list-header-fs)',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.88px',
        color: active ? 'var(--evo-cyan)' : 'var(--evo-fg-3)',
        textAlign: align,
        userSelect: 'none',
        outline: 'none',
        boxShadow: focused ? '0 0 0 2px var(--evo-cyan)' : 'none',
        transition: 'color 80ms, box-shadow 80ms',
      }}
    >
      {label}
      <SortArrow dir={dir} />
    </button>
  );
}
